import { HTMLMotionProps, Target, TargetAndTransition } from 'motion/react';

type Options = {
  initial?: Target;
  animate?: TargetAndTransition;
  exit?: TargetAndTransition;
};

type MotionDivProps = HTMLMotionProps<'div'> & {
  ref?: React.Ref<HTMLDivElement>;
};

/**
 * Expand height of a motion div from 0 to auto.
 */
export const expand = (options: Options = {}): MotionDivProps => {
  const { initial = {}, animate = {}, exit = { transition: {} } } = options;
  return {
    initial: {
      height: 0,
      opacity: 0,
      ...initial,
    },
    animate: {
      height: 'auto',
      opacity: 1,
      ...animate,
    },
    exit: {
      height: 0,
      opacity: 0,
      ...exit,
      transition: { ease: 'easeInOut', ...(exit.transition || {}) },
    },
  };
};

/**
 * Slides horizontal and fades in/out.
 */
export const slideHorizontal = (options: Options = {}): MotionDivProps => {
  const { initial = {}, animate = {}, exit = { transition: {} } } = options;
  return {
    initial: {
      translateX: -10,
      opacity: 0,
      ...initial,
    },
    animate: {
      translateX: 0,
      opacity: 1,
      ...animate,
    },
    exit: {
      translateX: -10,
      opacity: 0,
      ...exit,
      transition: { ease: 'easeInOut', ...(exit.transition || {}) },
    },
  };
};

/**
 * Add a slide in/out animation to a motion div.
 */
export const slideVertical = (options: Options = {}): MotionDivProps => {
  const { initial = {}, animate = {}, exit = { transition: {} } } = options;
  return {
    initial: {
      translateY: -10,
      opacity: 0,
      ...initial,
    },
    animate: {
      translateY: 0,
      opacity: 1,
      ...animate,
    },
    exit: {
      translateY: -10,
      opacity: 0,
      ...exit,
      transition: { ease: 'easeInOut', ...(exit.transition || {}) },
    },
  };
};

/** Slide in animation, bottom to top. */
export const slideVerticalReverse = (options: Options = {}) => {
  return slideVertical({
    ...options,
    initial: { translateY: 10, ...options?.initial },
    exit: { translateY: 10, ...options?.exit },
  });
};

/** Add a fade in/out animation to a motion div. */
export const fade = (options: Options = {}): MotionDivProps => {
  const { initial = {}, animate = {}, exit = { transition: {} } } = options;
  return {
    initial: {
      opacity: 0,
      ...initial,
    },
    animate: {
      opacity: 1,
      ...animate,
    },
    exit: {
      opacity: 0,
      ...exit,
      transition: { ease: 'easeInOut', ...(exit.transition || {}) },
    },
  };
};
